import type { ComponentProps, ReactNode } from 'react'
import { useId } from 'react'
import { cn } from '../cn'

export type InputProps = ComponentProps<'input'> & { invalid?: boolean }

export function Input({ className, invalid, required, ...props }: InputProps) {
  return (
    <input
      aria-invalid={invalid || undefined}
      aria-required={required || undefined}
      required={required}
      className={cn(
        'h-10 w-full rounded-[var(--radius-control)] border bg-[var(--ad-surface)] px-3 text-sm outline-none',
        'border-[var(--ad-border)] focus-visible:ring-3 focus-visible:ring-[var(--ad-ring)]',
        'aria-invalid:border-[var(--ad-danger)]',
        className,
      )}
      {...props}
    />
  )
}

export type FieldProps = {
  label: string
  /** Sunucudan gelen doğrulama hatası (ApiError.fieldErrors()). */
  error?: string
  hint?: ReactNode
  required?: boolean
  children: (props: { id: string; invalid: boolean; required: boolean }) => ReactNode
}

/**
 * Etiket, yardım metni ve hata mesajını bir arada tutar.
 *
 * Hata mesajı `aria-describedby` ile alana bağlanır: ekran okuyucu kullanan
 * biri hatayı alandan ayrı bir yerde değil, alanı odakladığında duyar.
 */
export function Field({ label, error, hint, required, children }: FieldProps) {
  const id = useId()
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
        {/* Yildiz yalnizca gorsel: erisilebilir ad 'Dernek adi*' olmamali.
            Zorunluluk ekran okuyucuya `aria-required` ile bildirilir. */}
        {required && (
          <span aria-hidden="true" className="ml-0.5 text-[var(--ad-danger)]">
            *
          </span>
        )}
      </label>
      <div aria-describedby={describedBy}>
        {children({ id, invalid: Boolean(error), required: Boolean(required) })}
      </div>
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-xs text-[var(--ad-danger)]">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-[var(--ad-text-muted)]">
          {hint}
        </p>
      ) : null}
    </div>
  )
}
